import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { initials, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Consultations() {
  const navigate = useNavigate();
  const [cons, setCons] = useState(null);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [patientId, setPatientId] = useState("");

  const load = () => api.get("/consultations").then((r) => setCons(r.data)).catch(() => setCons([]));
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)); }, []);

  const start = async () => {
    if (!patientId) return toast.error("Select a patient");
    const p = patients.find((x) => x.id === patientId);
    try {
      const { data } = await api.post("/consultations", { patient_id: patientId, patient_name: p?.name, chief_complaint: p?.chief_complaint || "" });
      setOpen(false);
      navigate(`/consultations/${data.id}`);
    } catch { toast.error("Could not start consultation"); }
  };

  const StartDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-consultation-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><Plus className="h-4 w-4 mr-1.5" />New Consultation</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Start Consultation</DialogTitle></DialogHeader>
        <div className="py-2">
          <Label className="text-xs font-semibold">Patient</Label>
          <Select value={patientId} onValueChange={setPatientId}>
            <SelectTrigger className="mt-1" data-testid="consultation-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
            <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.patient_id}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter><Button onClick={start} data-testid="start-consultation-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Begin guided consultation</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div>
      <PageHeader title="Consultations" subtitle="Guided clinical consultation workflows" breadcrumb={["Workspace", "Consultations"]} actions={StartDialog} />
      {cons && cons.length === 0 ? (
        <EmptyState icon={Stethoscope} title="No consultations yet" description="Start a guided consultation to capture chief complaint, findings, diagnosis and plan." actionLabel="New Consultation" onAction={() => setOpen(true)} testId="consultations-empty" />
      ) : (
        <div className="space-y-3">
          {(cons || []).map((c) => (
            <SurfaceCard key={c.id} className="p-4 cursor-pointer hover:border-[#00A85A]/40" onClick={() => navigate(`/consultations/${c.id}`)}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-xs font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(c.patient_name)}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-bold text-[#101516] dark:text-white">{c.patient_name}</div><div className="text-xs text-[#8B9698] truncate">{c.chief_complaint || "New consultation"} · {formatDate(c.created_at)}</div></div>
                <StatusBadge status={c.status} />
                <ChevronRight className="h-4 w-4 text-[#8B9698]" />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
